/**
 * @file driver-ledger.tsx
 * @module app/modules/customers/driver-ledger
 * @description Displays driver ledger records with filtering, pagination, permission checks,
 * and Excel export of the filtered results.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  Col,
  FormLabel,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { KTSVG } from "../../../_admin/helpers";
import APICallService from "../../../api/apiCallService";
import { driver, buyer } from "../../../api/apiEndPoints";
import Loader from "../../../Global/loader";
import Pagination from "../../../Global/pagination";
import {
  DriverLedger,
  PAGE_LIMIT,
  View,
  AllCustomers,
  ORDER_PAGE_LIMIT,
  Admin,
} from "../../../utils/constants";
import b1 from "../../../_admin/assets/media/product/default.svg";
import Method from "../../../utils/methods";
import { useAuth } from "../auth";
import { useWarehouse } from "../../context/WarehouseContext";
import { IGetCustomers } from "../../../types/responseIndex";
import clsx from "clsx";
import OrderPagination from "../../../Global/order-pagination";
import PermissionModal from "../../modals/permission";
import * as XLSX from "xlsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { getKey, removeKey, setKey } from "../../../Global/history";
import { useNavigationHistory } from "../../../utils/useNavigationHistory";

const ExcelExportModal = ({
  show,
  onHide,
  searchTerm,
  sortKey,
  sortOrder,
}: {
  show: boolean;
  onHide: () => void;
  searchTerm: string;
  sortKey: string;
  sortOrder: number;
}) => {
  const { selectedWarehouse } = useWarehouse();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [processedRecords, setProcessedRecords] = useState(0);
  const [error, setError] = useState("");

  const CHUNK_SIZE = 100;

  const handleExport = async () => {
    setIsExporting(true);
    setError("");
    setProgress(0);
    setCurrentStep("Initializing export...");

    try {
      // First, get total count using existing API structure
      setCurrentStep("Calculating total records...");

      let countParams: any = {
        pageNo: 1,
        sortKey: sortKey,
        sortOrder: sortOrder,
        searchTerm: searchTerm,
        needCount: true,
        state: 1,
      };
      if (selectedWarehouse?.value) {
        countParams.warehouse = selectedWarehouse?.value;
      }

      let countApiService = new APICallService(
        driver.listDriver,
        countParams,
        "",
        "",
        "",
        "",
        DriverLedger,
        selectedWarehouse?.value || null
      );
      let countResponse = await countApiService.callAPI();

      if (!countResponse || !countResponse?.total) {
        setError("Failed to get total count");
        return;
      }

      const total = countResponse?.total;
      setTotalRecords(total);

      if (total === 0) {
        setError("No records found to export");
        return;
      }

      // Warn user for large datasets
      if (total > 5000) {
        setCurrentStep(
          `Large dataset detected (${total.toLocaleString()} records). This may take several minutes...`
        );
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // Prepare Excel workbook
      const workbook = XLSX.utils.book_new();

      // Process in chunks
      const totalChunks = Math.ceil(total / CHUNK_SIZE);
      let processedRecords = 0;

      for (let page = 1; page <= totalChunks; page++) {
        setCurrentStep(
          `Processing records ${processedRecords + 1} to ${Math.min(
            processedRecords + CHUNK_SIZE,
            total
          )} of ${total}...`
        );

        try {
          // Fetch driver chunk
          let chunkParams: any = {
            pageNo: page,
            limit: CHUNK_SIZE,
            sortKey: sortKey,
            sortOrder: sortOrder,
            searchTerm: searchTerm,
            needCount: false,
            state: 1,
          };
          if (selectedWarehouse?.value) {
            chunkParams.warehouse = selectedWarehouse.value;
          }

          let chunkApiService = new APICallService(
            driver.listDriver,
            chunkParams,
            "",
            "",
            "",
            "",
            DriverLedger,
            selectedWarehouse?.value || null
          );
          let chunkResponse = await chunkApiService.callAPI();

          if (!chunkResponse || !chunkResponse?.records) {
            throw new Error(`Failed to fetch chunk ${page}`);
          }

          // Process each driver in the chunk
          for (const driverData of chunkResponse?.records) {
            setCurrentStep(
              `Processing driver: ${driverData?.name || "Unknown Driver"}...`
            );

            // Create worksheet for this driver
            const worksheetData = [];

            // Add driver summary section headers
            const driverHeaders = [
              "Total Orders",
              "Total Amount (TSh)",
              "Total Amount Collected (TSh)",
              "Pending Cash Deposit (TSh)",
              "Total Cash Deposited (TSh)",
              "Remaining Amount (TSh)",
            ];
            worksheetData.push(driverHeaders);

            // Add driver summary row
            const analytics =
              driverData?.analytics?.length && driverData?.analytics[0]?.length
                ? driverData.analytics[0][0]
                : null;
            const driverSummaryRow = [
              analytics?.totalOrders || 0,
              analytics?.totalAmountToBeCollected || 0,
              analytics?.totalAmountCollected || 0,
              analytics?.pendingCashDeposit || 0,
              analytics?.totalCashDeposited || 0,
              analytics?.totalRemainingAmount || 0,
            ];
            worksheetData.push(driverSummaryRow);

            // Add empty rows for separation
            worksheetData.push(["", "", "", "", "", ""]);
            worksheetData.push(["", "", "", "", "", ""]);

            // Fetch customer data for this driver
            try {
              setCurrentStep(
                `Fetching customers for driver: ${
                  driverData.name || "Unknown Driver"
                }...`
              );

              let customerParams: any = {
                driver: driverData?._id,
                status: 1,
              };
              if (selectedWarehouse?.value) {
                customerParams.warehouse = selectedWarehouse.value;
              }

              let customerApiService = new APICallService(
                buyer.receivablePayouts,
                customerParams,
                "",
                "",
                "",
                "",
                DriverLedger,
                selectedWarehouse?.value || null
              );
              let customerResponse = await customerApiService.callAPI();

              if (
                customerResponse &&
                customerResponse.records &&
                customerResponse.records.length > 0
              ) {
                // Group orders by customer
                const customerGroups = customerResponse.records.reduce(
                  (acc: any, order: any) => {
                    const customerId = order.customer?.reference;
                    if (!acc[customerId]) {
                      acc[customerId] = {
                        customer: order.customer,
                        orders: [],
                        totalAmount: 0,
                        remainingCharge: 0,
                      };
                    }
                    acc[customerId].orders.push(order);
                    acc[customerId].totalAmount +=
                      order.payment?.totalCharge || 0;
                    acc[customerId].remainingCharge +=
                      order.payment?.remainingCharge || 0;
                    return acc;
                  },
                  {}
                );

                // Process each customer
                for (const [customerId, customerData] of Object.entries(
                  customerGroups
                )) {
                  const customer = (customerData as any).customer;
                  const orders = (customerData as any).orders;
                  const totalAmount = (customerData as any).totalAmount;
                  const remainingCharge = (customerData as any).remainingCharge;

                  // Add customer section headers if this is the first customer
                  if (Object?.keys(customerGroups)?.indexOf(customerId) === 0) {
                    const customerHeaders = [
                      "Customer Name",
                      "Business Name",
                      "Customer Orders",
                      "Customer Total Amount (TSh)",
                      "Customer Remaining Amount (TSh)",
                      "Order Date",
                      "Order ID",
                      "Order Total (TSh)",
                      "Paid Amount (TSh)",
                      "Order Remaining (TSh)",
                    ];
                    worksheetData.push(customerHeaders);
                    worksheetData.push([
                      "",
                      "",
                      "",
                      "",
                      "",
                      "",
                      "",
                      "",
                      "",
                      "",
                    ]); // Empty row for separation
                  }

                  // Add customer summary row
                  const customerSummaryRow = [
                    customer.name ||
                      customer.business?.name ||
                      "Unknown Customer",
                    customer.business?.name || "",
                    orders.length,
                    totalAmount,
                    remainingCharge,
                    "", // Empty for order date in customer summary row
                    "", // Empty for order ID in customer summary row
                    "", // Empty for order total in customer summary row
                    "", // Empty for paid amount in customer summary row
                    "", // Empty for order remaining in customer summary row
                  ];
                  worksheetData?.push(customerSummaryRow);

                  // Add order details for this customer
                  orders?.forEach((order: any) => {
                    const orderDetailRow = [
                      "", // Empty customer name for order rows
                      "", // Empty business name for order rows
                      "", // Empty customer orders for order rows
                      "", // Empty customer total amount for order rows
                      "", // Empty customer remaining amount for order rows
                      order._createdAt
                        ? Method.convertDateToDDMMYYYY(order._createdAt)
                        : order.createdAt
                        ? Method.convertDateToDDMMYYYY(order.createdAt)
                        : "",
                      order.refKey || order.orderId || order._id || "N/A",
                      order.payment?.totalCharge || 0,
                      order.payment?.receivedCharge || 0,
                      order.payment?.remainingCharge || 0,
                    ];
                    worksheetData?.push(orderDetailRow);
                  });
                }
              } else {
                // Driver with no customers - add customer section headers and empty row
                const customerHeaders = [
                  "Customer Name",
                  "Business Name",
                  "Customer Orders",
                  "Customer Total Amount (TSh)",
                  "Customer Remaining Amount (TSh)",
                  "Order Date",
                  "Order ID",
                  "Order Total (TSh)",
                  "Paid Amount (TSh)",
                  "Order Remaining (TSh)",
                ];
                worksheetData?.push(customerHeaders);
                worksheetData?.push(["", "", "", "", "", "", "", "", "", ""]); // Empty row for separation

                const emptyCustomerRow = [
                  "No customers",
                  "",
                  0,
                  0,
                  0,
                  "",
                  "",
                  0,
                  0,
                  0,
                ];
                worksheetData?.push(emptyCustomerRow);
              }

              // Small delay to prevent overwhelming the server
              await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (customerError) {
              console.error(
                `Error fetching customers for driver ${driverData?._id}:`,
                customerError
              );
              // Add customer section headers and error row if there's an error
              const customerHeaders = [
                "Customer Name",
                "Business Name",
                "Customer Orders",
                "Customer Total Amount (TSh)",
                "Customer Remaining Amount (TSh)",
                "Order Date",
                "Order ID",
                "Order Total (TSh)",
                "Paid Amount (TSh)",
                "Order Remaining (TSh)",
              ];
              worksheetData.push(customerHeaders);
              worksheetData.push(["", "", "", "", "", "", "", "", "", ""]); // Empty row for separation

              const emptyCustomerRow = [
                "Error fetching customers",
                "",
                0,
                0,
                0,
                "",
                "",
                0,
                0,
                0,
              ];
              worksheetData.push(emptyCustomerRow);
            }

            // Create worksheet for this driver
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            // Set column widths for driver section (6 columns)
            const driverCols = [
              { width: 15 }, // Total Orders
              { width: 20 }, // Total Amount
              { width: 25 }, // Total Amount Collected
              { width: 25 }, // Pending Cash Deposit
              { width: 25 }, // Total Cash Deposited
              { width: 20 }, // Remaining Amount
            ];

            // Set column widths for customer section (10 columns)
            const customerCols = [
              { width: 25 }, // Customer Name
              { width: 25 }, // Business Name
              { width: 15 }, // Customer Orders
              { width: 25 }, // Customer Total Amount
              { width: 25 }, // Customer Remaining Amount
              { width: 15 }, // Order Date
              { width: 20 }, // Order ID
              { width: 15 }, // Order Total
              { width: 15 }, // Paid Amount
              { width: 18 }, // Order Remaining
            ];

            // Combine column widths
            worksheet["!cols"] = [...driverCols, ...customerCols];

            // Add styling for hierarchical structure
            const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

            // Style header row
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
              if (!worksheet[cellRef]) continue;
              worksheet[cellRef].s = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "4472C4" } },
                alignment: { horizontal: "center" },
              };
            }

            // Style driver summary rows (rows with driver data)
            for (let row = 1; row <= range.e.r; row++) {
              const totalOrdersCell = XLSX.utils.encode_cell({ r: row, c: 0 });
              if (
                worksheet[totalOrdersCell] &&
                worksheet[totalOrdersCell].v !== "" &&
                typeof worksheet[totalOrdersCell].v === "number"
              ) {
                // This is a driver summary row
                for (let col = 0; col <= 5; col++) {
                  const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                  if (worksheet[cellRef]) {
                    worksheet[cellRef].s = {
                      font: { bold: true },
                      fill: { fgColor: { rgb: "E7E6E6" } },
                    };
                  }
                }
              }
            }

            // Style customer summary rows (rows with customer data)
            for (let row = 1; row <= range.e.r; row++) {
              const customerNameCell = XLSX.utils.encode_cell({ r: row, c: 6 });
              if (
                worksheet[customerNameCell] &&
                worksheet[customerNameCell].v !== "" &&
                worksheet[customerNameCell].v !== "No customers" &&
                worksheet[customerNameCell].v !== "Error fetching customers"
              ) {
                // This is a customer summary row
                for (let col = 6; col <= 10; col++) {
                  const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                  if (worksheet[cellRef]) {
                    worksheet[cellRef].s = {
                      font: { bold: true },
                      fill: { fgColor: { rgb: "F0F0F0" } },
                    };
                  }
                }
              }
            }

            // Add sheet to workbook with driver name as sheet name
            const sheetName = (driverData.name || "Driver")
              .replace(/[\\/*?:[\]]/g, "_")
              .substring(0, 31);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
          }

          processedRecords += chunkResponse.records.length;
          setProcessedRecords(processedRecords);
          setProgress((processedRecords / total) * 100);

          // Small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (chunkError) {
          console.error(`Error processing chunk ${page}:`, chunkError);
          setError(
            `Failed to process chunk ${page}. Some data may be missing.`
          );
          // Continue with next chunk instead of failing completely
        }
      }

      // Create and download Excel file
      setCurrentStep("Generating Excel file...");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `driver-ledger-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCurrentStep("Export completed successfully!");
      setTimeout(() => {
        onHide();
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      setError("Export failed: " + (error as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (isExporting) {
      if (
        window.confirm(
          "Export is in progress. Are you sure you want to cancel?"
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={isExporting ? "static" : true}
      keyboard={!isExporting}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!isExporting}>
        <Modal.Title>Export Driver Ledger</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isExporting && !error && (
          <div>
            <div className="alert alert-info">
              <h6>
                <strong>Export Information:</strong>
              </h6>
              <ul className="mb-2">
                <li>This will export all driver ledger data to Excel format</li>
                <li>Each driver will have a separate sheet named after them</li>
                <li>Large datasets may take several minutes to process</li>
                <li>Please keep this window open during export</li>
                <li>The file will automatically download when ready</li>
              </ul>
            </div>

            <div className="alert alert-warning">
              <strong>Note:</strong> For datasets with 10,000+ records, the
              export may take 5-10 minutes. Please be patient and do not close
              this window.
            </div>
          </div>
        )}

        {isExporting && (
          <div>
            <div className="d-flex align-items-center mb-3">
              <Spinner animation="border" size="sm" className="me-3" />
              <strong>Exporting Data...</strong>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Progress:</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="progress">
                <div
                  className={`progress-bar ${
                    progress === 100 ? "bg-success" : "bg-primary"
                  }`}
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>

            {totalRecords > 0 && (
              <div className="mb-3">
                <small className="text-muted">
                  Processed: {processedRecords.toLocaleString()} of{" "}
                  {totalRecords.toLocaleString()} records
                </small>
              </div>
            )}

            <div className="mb-2">
              <small>{currentStep}</small>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <strong>Error:</strong> {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!isExporting && (
          <>
            <Button className="uw-btn uw-btn-light" variant="secondary" data-analytics-event="customers_driver_ledger_export_cancel_clicked" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="uw-btn uw-btn-primary" variant="primary" data-analytics-event="customers_driver_ledger_export_start_clicked" onClick={handleExport}>
              Start Export
            </Button>
          </>
        )}

        {isExporting && (
          <Button className="uw-btn uw-btn-light" variant="outline-secondary" data-analytics-event="customers_driver_ledger_export_cancel_running_clicked" onClick={handleClose}>
            Cancel Export
          </Button>
        )}

        {error && (
          <Button className="uw-btn uw-btn-primary"
            variant="primary"
            data-analytics-event="customers_driver_ledger_export_try_again_clicked"
            onClick={() => {
              setError("");
              setProgress(0);
              setCurrentStep("");
            }}
          >
            Try Again
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const DriverLedgerPage = () => {
  const { currentUser }: any = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(ORDER_PAGE_LIMIT);
  const [drivers, setDrivers] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [driverCustomerData, setDriverCustomerData] = useState<any>({});
  const [customerOrderData, setCustomerOrderData] = useState<any>({});
  const [loadingCustomer, setLoadingCustomer] = useState<any>({});
  const [loadingOrders, setLoadingOrders] = useState<any>({});
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [sortKey, setSortKey] = useState("totalAmount");
  const [sortOrder, setSortOrder] = useState(-1);
  const [showExportModal, setShowExportModal] = useState(false);
  const isInitialMount = useRef(true);

  console.log(customerOrderData, "99999");

  // Fetch data whenever warehouse changes (including initial load)
  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      const warehouseValue = selectedWarehouse?.value || null;
      await fetchDriverData(
        page,
        pageLimit,
        searchTerm,
        sortKey,
        sortOrder,
        warehouseValue
      );
      setFetchLoading(false);
    })();
  }, [selectedWarehouse]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      setPage(1);
      fetchDriverData(
        1,
        pageLimit,
        searchTerm,
        sortKey,
        sortOrder,
        selectedWarehouse?.value || null
      ).finally(() => setLoading(false));
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const fetchDriverData = async (
    pageNo: number,
    limit: number,
    search: string,
    sortField: string = sortKey,
    sortDirection: number = sortOrder,
    warehouseId?: any
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortField,
      sortOrder: sortDirection,
      searchTerm: search,
      needCount: true,
      state: 1, // Only active drivers
    };
    if (warehouseId) {
      params = { ...params, warehouse: warehouseId };
    }

    let apiService = new APICallService(
      driver.listDriver,
      params,
      "",
      "",
      "",
      "",
      DriverLedger,
      warehouseId
    );
    let response = await apiService.callAPI();
    if (response) {
      if (response.total) {
        setTotalRecords(response.total);
      }
      setDrivers(response.records);
    }
    setLoading(false);
  };

  const fetchDriverCustomers = async (driverId: string, warehouseId?: any) => {
    setLoadingCustomer({ ...loadingCustomer, [driverId]: true });

    let params: any = {
      driver: driverId,
      status: 1,
    };

    if (warehouseId) {
      params = { ...params, warehouse: warehouseId };
    }

    let apiService = new APICallService(
      buyer.receivablePayouts,
      params,
      "",
      "",
      "",
      "",
      DriverLedger,
      warehouseId
    );
    let response = await apiService.callAPI();
    if (response) {
      // Group orders by customer
      const customerGroups = response.records.reduce((acc: any, order: any) => {
        const customerId = order.customer?.reference;
        if (!acc[customerId]) {
          acc[customerId] = {
            customer: order.customer,
            orders: [],
            totalAmount: 0,
            remainingCharge: 0,
          };
        }
        acc[customerId].orders.push(order);
        acc[customerId].totalAmount += order.payment?.totalCharge || 0;
        acc[customerId].remainingCharge += order.payment?.remainingCharge || 0;
        return acc;
      }, {});

      setDriverCustomerData({
        ...driverCustomerData,
        [driverId]: Object.values(customerGroups),
      });
    }
    setLoadingCustomer({ ...loadingCustomer, [driverId]: false });
  };

  const fetchCustomerOrders = async (
    customerId: string,
    driverId: string,
    warehouseId?: any
  ) => {
    setLoadingOrders({ ...loadingOrders, [customerId]: true });

    let params: any = {
      customer: customerId,
      driver: driverId,
      status: 1,
    };

    if (warehouseId) {
      params = { ...params, warehouse: warehouseId };
    }

    let apiService = new APICallService(
      buyer.receivablePayouts,
      params,
      "",
      "",
      "",
      "",
      DriverLedger,
      warehouseId
    );
    let response = await apiService.callAPI();
    if (response) {
      setCustomerOrderData({
        ...customerOrderData,
        [customerId]: response.records,
      });
    }
    setLoadingOrders({ ...loadingOrders, [customerId]: false });
  };

  const handleCurrentPage = async (val: number) => {
    setLoading(true);
    setPage(val);
    await fetchDriverData(
      val,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedWarehouse?.value || null
    );
    setLoading(false);
  };

  const handleNextPage = async (val: number) => {
    setLoading(true);
    setPage(val + 1);
    await fetchDriverData(
      val + 1,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedWarehouse?.value || null
    );
    setLoading(false);
  };

  const handlePreviousPage = async (val: number) => {
    setLoading(true);
    setPage(val - 1);
    await fetchDriverData(
      val - 1,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedWarehouse?.value || null
    );
    setLoading(false);
  };

  const handlePageLimit = async (event: any) => {
    setLoading(true);
    setPage(1);
    await setPageLimit(parseInt(event.target.value));
    await fetchDriverData(
      1,
      event.target.value,
      searchTerm,
      sortKey,
      sortOrder,
      selectedWarehouse?.value || null
    );
    setLoading(false);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // The actual search is handled by the debounced useEffect
  };

  const handleSort = async (field: string) => {
    let newSortOrder = 1; // Default to ascending
    if (sortKey === field) {
      newSortOrder = sortOrder === 1 ? -1 : 1; // Toggle between ascending and descending
    }

    setSortKey(field);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when sorting

    setExpandedDriver([]);
    setExpandedNestedDriver([]);

    await fetchDriverData(
      1,
      pageLimit,
      searchTerm,
      field,
      newSortOrder,
      selectedWarehouse?.value || null
    );
  };

  const getSortIcon = (field: string) => {
    if (sortKey !== field) {
      return (
        <KTSVG
          path="/media/icons/duotune/arrows/arr003.svg"
          className="svg-icon-2 svg-icon-gray-400 ms-1"
        />
      );
    }

    return sortOrder === 1 ? (
      <KTSVG
        path="/media/icons/duotune/arrows/arr004.svg"
        className="svg-icon-2 svg-icon-gray-400 ms-1"
      />
    ) : (
      <KTSVG
        path="/media/icons/duotune/arrows/arr003.svg"
        className="svg-icon-2 svg-icon-gray-400 ms-1"
      />
    );
  };

  // expand collapse table logic here

  const [expandedDriver, setExpandedDriver] = useState(
    new Array(drivers.length).fill(false)
  );

  const [expandedNestedDriver, setExpandedNestedDriver] = useState(
    new Array(drivers.length).fill(false)
  );

  const toggleDriverDetails = (index: number) => {
    const newExpandedDriver = [...expandedDriver];
    newExpandedDriver[index] = !newExpandedDriver[index];
    setExpandedDriver(newExpandedDriver);
  };

  const toggleNestedDriverDetails = (index: number) => {
    const newExpandedDriver = [...expandedNestedDriver];
    newExpandedDriver[index] = !newExpandedDriver[index];
    setExpandedNestedDriver(newExpandedDriver);
  };

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center mb-5">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-0">Driver Ledger</h1>
          </Col>
          <Col xs="auto">
            <Button
              variant="success"
              data-analytics-event="customers_driver_ledger_export_excel_clicked"
              onClick={() => setShowExportModal(true)}
              className="uw-btn uw-btn-success me-3"
            >
              <KTSVG
                path="/media/icons/duotune/files/fil003.svg"
                className="svg-icon-2 me-2"
              />
              Export to Excel
            </Button>
          </Col>
        </Row>
        {showPermissionModal && (
          <PermissionModal
            show={showPermissionModal}
            onHide={() => setShowPermissionModal(false)}
            moduleName={"orders & delivery"}
          />
        )}
        {/* Search Bar */}
        <Card className="border bg-light mb-4">
          <Card.Body className="px-7">
            <Row className="align-items-center">
              <Col md={6}>
                <div>
                  <div className="d-flex align-items-center min-w-md-250px w-100 w-fit-content position-relative me-lg-4">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-3 position-absolute ps-5"
                    />
                    <input
                      type="text"
                      id="kt_filter_search"
                      value={searchTerm}
                      className="form-control form-control-custom bg-white min-h-30px ps-13 text-dark"
                      placeholder="Search by driver name, phone, email..."
                      onChange={(event: any) => {
                        handleSearch(event.target.value);
                      }}
                    />
                  </div>
                </div>
              </Col>
              <Col md={6}></Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Expand collapse table */}

        <div className="table-responsive border rounded">
          {!loading && drivers.length === 0 ? (
            <div className="text-center py-7">
              <div className="fs-16 text-muted">No drivers found</div>
            </div>
          ) : (
            <table
              className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0"
              style={{ tableLayout: "auto" }}
            >
              <thead>
                <tr className="fs-16 fw-600 h-65px align-middle">
                  <th className="min-w-150px">Driver</th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Total Orders{" "}
                    <span onClick={() => handleSort("totalOrders")}>
                      {getSortIcon("totalOrders")}
                    </span>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Total Amount{" "}
                    <span
                      onClick={() => handleSort("totalAmountToBeCollected")}
                    >
                      {getSortIcon("totalAmountToBeCollected")}
                    </span>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    <div className="d-flex align-items-center">
                      <div>
                        <div>Total Amount Collected</div>
                        <div className="fs-5 fw-400">
                          {"(Cash Payment + Online Payment)"}
                        </div>
                      </div>
                      <div onClick={() => handleSort("totalAmountCollected")}>
                        {getSortIcon("totalAmountCollected")}
                      </div>
                    </div>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Pending Deposits{" "}
                    <span onClick={() => handleSort("pendingCashDeposit")}>
                      {getSortIcon("pendingCashDeposit")}
                    </span>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Total Deposits{" "}
                    <span onClick={() => handleSort("totalCashDeposited")}>
                      {getSortIcon("totalCashDeposited")}
                    </span>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Remaining Amount{" "}
                    <span onClick={() => handleSort("totalRemainingAmount")}>
                      {getSortIcon("totalRemainingAmount")}
                    </span>
                  </th>
                  <th className="text-end"></th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "#f9f9f9" }}>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-7">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : (
                  drivers?.map((driver: any, driverIndex: any) => (
                    <React.Fragment key={driver._id}>
                      <tr
                        className={
                          expandedDriver[driverIndex] && "text-primary"
                        }
                        onClick={() => {
                          toggleDriverDetails(driverIndex);
                          if (!driverCustomerData[driver._id]) {
                            fetchDriverCustomers(
                              driver._id,
                              selectedWarehouse?.value || null
                            );
                          }
                        }}
                      >
                        <td>
                          <span className="fs-15 fw-600">{driver.name}</span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {driver?.analytics?.length &&
                            driver.analytics[0]?.length &&
                            driver.analytics[0][0]?.totalOrders
                              ? driver.analytics[0][0].totalOrders
                              : 0}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {driver?.analytics?.length &&
                            driver.analytics[0]?.length &&
                            driver.analytics[0][0]?.totalAmountToBeCollected
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  driver.analytics[0][0]
                                    .totalAmountToBeCollected
                                )
                              : "TSh " + 0}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {driver?.analytics?.length &&
                            driver.analytics[0]?.length &&
                            driver.analytics[0][0]?.totalAmountCollected
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  driver.analytics[0][0].totalAmountCollected
                                )
                              : "TSh " + 0}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {driver?.analytics?.length &&
                            driver.analytics[0]?.length &&
                            driver.analytics[0][0]?.pendingCashDeposit
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  driver.analytics[0][0].pendingCashDeposit
                                )
                              : "TSh " + 0}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {driver?.analytics?.length &&
                            driver.analytics[0]?.length &&
                            driver.analytics[0][0]?.totalCashDeposited
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  driver.analytics[0][0].totalCashDeposited
                                )
                              : "TSh " + 0}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {driver?.analytics?.length &&
                            driver.analytics[0]?.length &&
                            driver.analytics[0][0]?.totalRemainingAmount
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  driver.analytics[0][0].totalRemainingAmount
                                )
                              : "TSh " + 0}
                          </span>
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            className="p-0 me-2 text-muted"
                            data-analytics-event="customers_driver_ledger_expand_driver_clicked"
                            onClick={() => {
                              toggleDriverDetails(driverIndex);
                              if (!driverCustomerData[driver._id]) {
                                fetchDriverCustomers(
                                  driver._id,
                                  selectedWarehouse?.value || null
                                );
                              }
                            }}
                          >
                            <i
                              className={`fas fa-chevron-${
                                expandedDriver[driverIndex] ? "up" : "down"
                              }`}
                            ></i>
                          </Button>
                        </td>
                      </tr>

                      {/* Expanded Row for Driver Details (Level 2) */}
                      {expandedDriver[driverIndex] && (
                        <tr>
                          <td colSpan={8}>
                            <div className="table-responsive border rounded">
                              {!loadingCustomer[driver._id] &&
                              driverCustomerData[driver._id] &&
                              driverCustomerData[driver._id]?.length === 0 ? (
                                <div className="text-center py-7">
                                  <div className="fs-16 text-muted">
                                    No data found
                                  </div>
                                </div>
                              ) : (
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead className="bg-white">
                                    <tr className="fs-16 fw-600 h-65px align-middle">
                                      <th className="min-w-150px">
                                        Customer Name
                                      </th>
                                      <th className="min-w-150px">
                                        Total Orders
                                      </th>
                                      <th className="min-w-150px">
                                        Total Amount
                                      </th>
                                      <th className="min-w-150px">
                                        Remaining Amount
                                      </th>
                                      <th className="text-end"></th>
                                    </tr>
                                  </thead>
                                  {loadingCustomer[driver._id] ? (
                                    <div className="d-flex justify-content-center m-4 align-items-center">
                                      <Loader
                                        loading={loadingCustomer[driver._id]}
                                      />
                                    </div>
                                  ) : (
                                    <tbody>
                                      {driverCustomerData[driver._id]?.map(
                                        (
                                          customerData: any,
                                          customerIndex: number
                                        ) => (
                                          <React.Fragment
                                            key={
                                              customerData.customer.reference
                                            }
                                          >
                                            <tr
                                              onClick={() => {
                                                toggleNestedDriverDetails(
                                                  customerIndex
                                                ); // Toggle customer-level details based on customer ID
                                                if (
                                                  !customerOrderData[
                                                    customerData.customer
                                                      .reference
                                                  ]
                                                ) {
                                                  fetchCustomerOrders(
                                                    customerData.customer
                                                      .reference,
                                                    driver._id,
                                                    selectedWarehouse?.value
                                                  );
                                                }
                                              }}
                                            >
                                              <td>
                                                {" "}
                                                {customerData.customer.business
                                                  ?.name ||
                                                  customerData.customer.name}
                                              </td>
                                              <td>
                                                {customerData.orders?.length}{" "}
                                                orders
                                              </td>
                                              <td>
                                                {"TSh " +
                                                  Method.getGeneralizedAmount(
                                                    customerData.totalAmount
                                                  )}
                                              </td>
                                              <td>
                                                {"TSh " +
                                                  Method.getGeneralizedAmount(
                                                    customerData.remainingCharge
                                                  )}
                                              </td>
                                              <td className="text-end">
                                                <Button
                                                  variant="link"
                                                  className="p-0 me-2 text-muted"
                                                  data-analytics-event="customers_driver_ledger_expand_customer_clicked"
                                                  onClick={() => {
                                                    toggleNestedDriverDetails(
                                                      customerIndex
                                                    ); // Toggle customer-level details based on customer ID
                                                    if (
                                                      !customerOrderData[
                                                        customerData.customer
                                                          .reference
                                                      ]
                                                    ) {
                                                      fetchCustomerOrders(
                                                        customerData.customer
                                                          .reference,
                                                        driver._id,
                                                        selectedWarehouse?._id ||
                                                          null
                                                      );
                                                    }
                                                  }}
                                                >
                                                  <i
                                                    className={`fas fa-chevron-${
                                                      expandedNestedDriver[
                                                        customerIndex
                                                      ]
                                                        ? "up"
                                                        : "down"
                                                    }`}
                                                  ></i>
                                                </Button>
                                              </td>
                                            </tr>

                                            {/* Expanded Row for Nested Driver Details (Level 3) */}
                                            {expandedNestedDriver[
                                              customerIndex
                                            ] && (
                                              <tr>
                                                <td colSpan={8}>
                                                  <div className="table-responsive border rounded">
                                                    {customerOrderData[
                                                      customerData.customer
                                                        .reference
                                                    ]?.length === 0 ? (
                                                      <div className="text-center py-7">
                                                        <div className="fs-16 text-muted">
                                                          No data found
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                                                        <thead>
                                                          <tr className="fw-bold text-muted">
                                                            <th className="min-w-150px">
                                                              Order Date
                                                            </th>
                                                            <th className="min-w-140px">
                                                              Order ID
                                                            </th>
                                                            <th className="w-150px">
                                                              Total Amount
                                                            </th>
                                                            <th className="w-250px">
                                                              Paid Amount
                                                            </th>
                                                            <th className="w-350px">
                                                              Remaining Amount
                                                            </th>
                                                            <th className="w-150px"></th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {customerOrderData[
                                                            customerData
                                                              .customer
                                                              .reference
                                                          ]?.map(
                                                            (
                                                              order: any,
                                                              orderIndex: number
                                                            ) => (
                                                              <tr
                                                                key={order._id}
                                                              >
                                                                <td>
                                                                  <div className="d-flex align-items-center">
                                                                    <div className="d-flex justify-content-start flex-column">
                                                                      <span className="text-dark fw-bold fs-6">
                                                                        {Method.convertDateToDDMMYYYY(
                                                                          order._createdAt
                                                                        )}
                                                                      </span>
                                                                    </div>
                                                                  </div>
                                                                </td>
                                                                <td>
                                                                  <span className="text-dark fw-bold fs-6">
                                                                    {order.refKey ||
                                                                      "N/A"}
                                                                  </span>
                                                                </td>
                                                                <td>
                                                                  <span className="text-dark fw-bold fs-6">
                                                                    {"TSh " +
                                                                      Method.getGeneralizedAmount(
                                                                        order
                                                                          .payment
                                                                          .totalCharge
                                                                      )}
                                                                  </span>
                                                                </td>
                                                                <td>
                                                                  <span className="text-dark fw-bold fs-6">
                                                                    {"TSh " +
                                                                      Method.getGeneralizedAmount(
                                                                        order
                                                                          .payment
                                                                          .receivedCharge
                                                                      )}
                                                                  </span>
                                                                </td>
                                                                <td>
                                                                  {order.payment
                                                                    .remainingCharge ? (
                                                                    <span className="fw-bold fs-6 text-danger">
                                                                      {"TSh " +
                                                                        Method.getGeneralizedAmount(
                                                                          order
                                                                            .payment
                                                                            .remainingCharge
                                                                        )}
                                                                    </span>
                                                                  ) : (
                                                                    <span className="text-dark fw-bold fs-6">
                                                                      NA
                                                                    </span>
                                                                  )}
                                                                </td>
                                                                <td>
                                                                  {Method.hasPermission(
                                                                    DriverLedger,
                                                                    View,
                                                                    currentUser
                                                                  ) ? (
                                                                    <Button
                                                                      type="button"
                                                                      variant="primary"
                                                                      className="uw-btn uw-btn-primary uw-btn-sm btn-table"
                                                                      data-analytics-event="customers_driver_ledger_view_details_clicked"
                                                                      onClick={() => {
                                                                        navigateTo(
                                                                          "/orders/order-details",
                                                                          {
                                                                            state:
                                                                              {
                                                                                id: order._id,
                                                                                moduleName:
                                                                                  DriverLedger,
                                                                              },
                                                                          }
                                                                        );
                                                                      }}
                                                                    >
                                                                      View
                                                                      Details
                                                                    </Button>
                                                                  ) : (
                                                                    <Button
                                                                      type="button"
                                                                      variant="primary"
                                                                      className="uw-btn uw-btn-primary uw-btn-sm btn-table"
                                                                      data-analytics-event="customers_driver_ledger_view_details_clicked"
                                                                      onClick={() => {
                                                                        setShowPermissionModal(
                                                                          true
                                                                        );
                                                                      }}
                                                                    >
                                                                      View
                                                                      Details
                                                                    </Button>
                                                                  )}
                                                                </td>
                                                              </tr>
                                                            )
                                                          )}
                                                        </tbody>
                                                      </table>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        )
                                      )}
                                    </tbody>
                                  )}
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalRecords > 0 && drivers?.length > 0 && (
          <OrderPagination
            totalRecords={totalRecords}
            currentPage={page}
            handleCurrentPage={handleCurrentPage}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            handlePageLimit={handlePageLimit}
            pageLimit={pageLimit}
          />
        )}

        {/* Export Modal */}
        <ExcelExportModal
          show={showExportModal}
          onHide={() => setShowExportModal(false)}
          searchTerm={searchTerm}
          sortKey={sortKey}
          sortOrder={sortOrder}
        />
      </div>
    </>
  );
};

export default DriverLedgerPage;
